<template>
  <b-card>
    <b-alert v-if="err" show variant="danger">
      <p><pre>{{ err }}</pre></p>
    </b-alert>
    <template v-else-if="!company">
      <no-ssr><loading-spinner class="d-flex justify-content-center my-5"/></no-ssr>
    </template>
    <template v-else>
      <h3>Sign Up & Onboarding</h3>
      <p>You can customise the Sign Up and Onboarding process of the platform</p>

      <form-block v-for="(block, key) in blocks" :key="key" class="py-3"
        :title="block.title" :description="block.description" :company="company"
        :err="formErrs[key]" :state="formStates[key]"
        :fields="block.fields" :value="getValues(key)" @input="values => saveValues(key, values)"/>
    </template>
  </b-card>
</template>

<script>
import _get from 'lodash/get';
import gql from 'graphql-tag';
import FormBlock from '@/components/settings/FormBlock';
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import AssetDetailsFragment from '@/graphql/fragments/AssetDetails.gql';

const BLOCKS = {
  customiseSignupAndLoginPages: {
    title: 'Registration & Login',
    description: 'Customise text on the login & register pages of the platform',
    fields: [
      {
        key: 'template.REGISTER_FORM_TITLE',
        label: 'Register Form Title',
        type: 'template',
      },
      {
        key: 'template.REGISTER_FORM_DESCRIPTION',
        label: 'Register Form Description',
        type: 'template',
      },
      {
        key: 'template.REGISTER_INFO_TITLE',
        label: 'Register Info Title',
        type: 'template',
      },
      {
        key: 'template.REGISTER_INFO_CONTENT',
        label: 'Register Info Content',
        type: 'template',
        description: 'Each new line will be a bullet point',
      },
      {
        key: 'template.LOGIN_FORM_TITLE',
        label: 'Login Form Title',
        type: 'template',
      },
      {
        key: 'template.LOGIN_FORM_DESCRIPTION',
        label: 'Login Form Description',
        type: 'template',
      },
    ],
  },
  customiseOnboardingSteps: {
    title: 'Onboarding Steps',
    description: 'Customise the order of onboarding steps',
    fields: [
      {
        key: 'branding.onboarding',
        label: 'Onboarding Steps Order',
        type: 'onboardingSteps',
      },
    ],
  },
  customiseDashboardLayout: {
    title: 'Dashboard Layout',
    description: 'Customise the layout of the dashboard',
    fields: [
      {
        key: 'branding.dashboard',
        label: 'Dashboard Blocks Order',
        type: 'dashboardBlocks',
      },
    ],
  },
  customiseWelcomePage: {
    title: 'Welcome Page',
    description: 'Customise the content of the onboarding welcome page',
    fields: [
      {
        key: 'template.ONBOARDING_WELCOME_TITLE',
        label: 'Welcome Page Title',
        type: 'template',
      },
      {
        key: 'template.ONBOARDING_WELCOME_DESCRIPTION',
        label: 'Welcome Page Description',
        type: 'template',
      },
      {
        key: 'preferences.onboardingIntroVideoUrl',
        label: 'Onboarding Intro Video URL',
        type: 'string',
      },
    ],
  },
  customiseRegisterNetworkPage: {
    title: 'Referrals Page',
    description: 'Customise text on the register network page of the platform',
    fields: [
      {
        key: 'template.REGISTER_NETWORK_PAGE_TITLE',
        label: 'Referrals Page Title',
        type: 'template',
      },
      {
        key: 'template.REGISTER_NETWORK_PAGE_DESCRIPTION',
        label: 'Referrals Page Description',
        type: 'template',
      },
      {
        key: 'branding.onboardingRegisterNetworkImage',
        label: 'Introduction page left-hand side image',
        type: 'asset-image',
      },
    ],
  },
  onboardingMentorPreferencesPage: {
    title: 'Your Mentor Page',
    description: 'Customise text on the register network page of the platform',
    fields: [
      {
        key: 'template.ONBOARDING_MENTOR_PREFERENCES_TITLE',
        label: 'Your Mentor Page Title',
        type: 'template',
      },
      {
        key: 'template.ONBOARDING_MENTOR_PREFERENCES_DESCRIPTION',
        label: 'Your Mentor Page Description',
        type: 'template',
      },
      {
        key: 'branding.onboardingMentorPreferencesImage',
        label: 'Your mentor page left-hand side image',
        type: 'asset-image',
      },
    ],
  },
  customiseIntroductionPage: {
    title: 'Introduction Page',
    description: 'Customise text on the introduction page of the onboarding process',
    fields: [
      {
        key: 'template.ONBOARDING_INTRODUCTION_TITLE',
        label: 'Introduction Page Title',
        type: 'template',
      },
      {
        key: 'template.ONBOARDING_INTRODUCTION_DESCRIPTION',
        label: 'Introduction Page Description',
        type: 'template',
      },
      {
        key: 'template.ONBOARDING_INTRODUCTION_PREFERENCE_JOB_INTEREST_TITLE',
        label: 'Internal Jobs Interest Toggle Title',
        type: 'template',
      },
      {
        key: 'template.ONBOARDING_INTRODUCTION_PREFERENCE_JOB_INTEREST_DESCRIPTION',
        label: 'Internal Jobs Interest Toggle Description',
        type: 'template',
      },
      {
        key: 'template.ONBOARDING_INTRODUCTION_PREFERENCE_PROJECT_INTEREST_TITLE',
        label: 'Internal Projects Interest Toggle Title',
        type: 'template',
      },
      {
        key: 'template.ONBOARDING_INTRODUCTION_PREFERENCE_PROJECT_INTEREST_DESCRIPTION',
        label: 'Internal Projects Interest Toggle Description',
        type: 'template',
      },
      {
        key: 'template.ONBOARDING_INTRODUCTION_PREFERENCE_MENTOR_TITLE',
        label: 'Being a Mentor Interest Toggle Title',
        type: 'template',
      },
      {
        key: 'template.ONBOARDING_INTRODUCTION_PREFERENCE_MENTOR_DESCRIPTION',
        label: 'Being a Mentor Interest Toggle Description',
        type: 'template',
      },
      {
        key: 'template.ONBOARDING_INTRODUCTION_PREFERENCE_MENTORED_TITLE',
        label: 'Being Mentored Interest Toggle Title',
        type: 'template',
      },
      {
        key: 'template.ONBOARDING_INTRODUCTION_PREFERENCE_MENTORED_DESCRIPTION',
        label: 'Being Mentored Interest Toggle Description',
        type: 'template',
      },
      {
        key: 'branding.onboardingIntroductionImage',
        label: 'Left-hand side image',
        type: 'asset-image',
      },
    ],
  },
  customiseSkillsPage: {
    title: 'Skills Page',
    description: 'Customise text on the skills page of the onboarding process',
    fields: [
      {
        key: 'template.ONBOARDING_SKILLS_TITLE',
        label: 'Skills Page Title',
        type: 'template',
      },
      {
        key: 'template.ONBOARDING_SKILLS_DESCRIPTION',
        label: 'Skills Page Description',
        type: 'template',
      },
      {
        key: 'branding.onboardingSkillsImage',
        label: 'Left-hand side image',
        type: 'asset-image',
      },
    ],
  },
  customiseJobsPage: {
    title: 'Jobs Page',
    description: 'Customise text on the jobs page of the onboarding process',
    fields: [
      {
        key: 'template.ONBOARDING_JOB_INTEREST_TITLE',
        label: 'Jobs Page Title',
        type: 'template',
      },
      {
        key: 'template.ONBOARDING_JOB_INTEREST_DESCRIPTION',
        label: 'Jobs Page Description',
        type: 'template',
      },
      {
        key: 'branding.onboardingJobsImage',
        label: 'Left-hand side image',
        type: 'asset-image',
      },
    ],
  },
  customiseInterestPage: {
    title: 'Interest Page',
    description: 'Customise text on the interest page of the onboarding process',
    fields: [
      {
        key: 'template.ONBOARDING_INTEREST_TITLE',
        label: 'Interest Page Title',
        type: 'template',
      },
      {
        key: 'template.ONBOARDING_INTEREST_DESCRIPTION',
        label: 'Interest Page Description',
        type: 'template',
      },
      {
        key: 'branding.onboardingInterestImage',
        label: 'Left-hand side image',
        type: 'asset-image',
      },
    ],
  },
  customiseDevelopmentPage: {
    title: 'Development Page',
    description: 'Customise text on the development page of the onboarding process',
    fields: [
      {
        key: 'template.ONBOARDING_DEVELOPMENT_TITLE',
        label: 'Development Page Title',
        type: 'template',
      },
      {
        key: 'template.ONBOARDING_DEVELOPMENT_DESCRIPTION',
        label: 'Development Page Description',
        type: 'template',
      },
      {
        key: 'branding.onboardingDevelopmentImage',
        label: 'Left-hand side image',
        type: 'asset-image',
      },
    ],
  },
  customiseMentorshipPage: {
    title: 'Mentorship Page',
    description: 'Customise text on the mentorship page of the onboarding process',
    fields: [
      {
        key: 'template.ONBOARDING_MENTORSHIP_INTRO_TITLE',
        label: 'Mentorship Introduction Page Title',
        type: 'template',
      },
      {
        key: 'template.ONBOARDING_MENTORSHIP_INTRO_SUBTITLE',
        label: 'Mentorship Introduction Page Subtitle (short intro)',
        type: 'template',
      },
      {
        key: 'template.ONBOARDING_MENTORSHIP_INTRO_DESCRIPTION',
        label: 'Mentorship Introduction Page Description',
        type: 'template',
      },
      {
        key: 'branding.onboardingMentorshipIntroductionImage',
        label: 'Introduction page left-hand side image',
        type: 'asset-image',
      },
      {
        key: 'template.ONBOARDING_MENTORSHIP_TITLE',
        label: 'Mentorship Page Title',
        type: 'template',
      },
      {
        key: 'template.ONBOARDING_MENTORSHIP_DESCRIPTION',
        label: 'Mentorship Page Description',
        type: 'template',
      },
      {
        key: 'branding.onboardingMentorshipImage',
        label: 'Left-hand side image',
        type: 'asset-image',
      },
    ],
  },
};

const CompanyOnboardingSettingsFragment = gql`
  ${AssetDetailsFragment}

  fragment CompanyOnboardingSettings on Company {
    id
    name

    branding {
      onboardingIntroductionImage {
        ...AssetDetails
      }
      onboardingSkillsImage {
        ...AssetDetails
      }
      onboardingJobsImage {
        ...AssetDetails
      }
      onboardingDevelopmentImage {
        ...AssetDetails
      }
      onboardingMentorshipImage {
        ...AssetDetails
      }
      onboardingMentorshipIntroductionImage {
        ...AssetDetails
      }
      onboardingRegisterNetworkImage {
        ...AssetDetails
      }
      onboardingMentorPreferencesImage {
        ...AssetDetails
      }
      onboarding {
        name
      }
      dashboard {
        name
      }
    }

    preferences {
      onboardingIntroVideoUrl
    }

    templates(where: {
      keys: [
        "REGISTER_FORM_TITLE",
        "REGISTER_FORM_DESCRIPTION",
        "REGISTER_INFO_TITLE",
        "REGISTER_INFO_CONTENT",
        "LOGIN_FORM_TITLE",
        "LOGIN_FORM_DESCRIPTION",
        "REGISTER_NETWORK_PAGE_TITLE",
        "REGISTER_NETWORK_PAGE_DESCRIPTION",
        "ONBOARDING_WELCOME_TITLE",
        "ONBOARDING_WELCOME_DESCRIPTION",
        "ONBOARDING_INTRODUCTION_TITLE",
        "ONBOARDING_INTRODUCTION_DESCRIPTION",
        "ONBOARDING_INTRODUCTION_PREFERENCE_JOB_INTEREST_TITLE",
        "ONBOARDING_INTRODUCTION_PREFERENCE_JOB_INTEREST_DESCRIPTION",
        "ONBOARDING_INTRODUCTION_PREFERENCE_PROJECT_INTEREST_TITLE",
        "ONBOARDING_INTRODUCTION_PREFERENCE_PROJECT_INTEREST_DESCRIPTION",
        "ONBOARDING_INTRODUCTION_PREFERENCE_MENTOR_TITLE",
        "ONBOARDING_INTRODUCTION_PREFERENCE_MENTOR_DESCRIPTION",
        "ONBOARDING_INTRODUCTION_PREFERENCE_MENTORED_TITLE",
        "ONBOARDING_INTRODUCTION_PREFERENCE_MENTORED_DESCRIPTION",
        "ONBOARDING_SKILLS_TITLE",
        "ONBOARDING_SKILLS_DESCRIPTION",
        "ONBOARDING_JOB_INTEREST_TITLE",
        "ONBOARDING_JOB_INTEREST_DESCRIPTION",
        "ONBOARDING_INTEREST_TITLE",
        "ONBOARDING_INTEREST_DESCRIPTION",
        "ONBOARDING_DEVELOPMENT_TITLE",
        "ONBOARDING_DEVELOPMENT_DESCRIPTION",
        "ONBOARDING_MENTORSHIP_INTRO_TITLE",
        "ONBOARDING_MENTORSHIP_INTRO_SUBTITLE",
        "ONBOARDING_MENTORSHIP_INTRO_DESCRIPTION",
        "ONBOARDING_MENTORSHIP_TITLE",
        "ONBOARDING_MENTORSHIP_DESCRIPTION",
        "ONBOARDING_MENTOR_PREFERENCES_TITLE",
        "ONBOARDING_MENTOR_PREFERENCES_DESCRIPTION"
      ]
    }) {
      key
      lang
      type
      default
      placeholder
      value
    }
  }
  `;

const SettingsQuery = gql`
  ${CompanyOnboardingSettingsFragment}

  query SettingsPreferences {
    company {
      ...CompanyOnboardingSettings
    }
  }
  `;

const SettingsMutation = gql`
  ${CompanyOnboardingSettingsFragment}

  mutation SettingsPreferences($update: CompanyUpdateInput!) {
    updateCompany(update: $update) {
      ...CompanyOnboardingSettings
    }
  }
  `;

export default {
  name: 'CompanySignUpAndOnboardingSettings',
  components: {
    FormBlock,
    LoadingSpinner,
  },
  data: () => ({
    company: null,
    err: null,
    formErrs: Object.keys(BLOCKS).reduce((states, key) => {
      states[key] = null;
      return states;
    }, {}),
    formStates: Object.keys(BLOCKS).reduce((states, key) => {
      states[key] = null;
      return states;
    }, {}),
  }),
  metaInfo: {
    title: 'Sign Up & Onboarding Settings',
  },
  apollo: {
    company: {
      query: SettingsQuery,
      error(err) { this.err = `${err}`; },
    },
  },
  computed: {
    blocks: () => BLOCKS,
  },
  methods: {
    getValues(key) {
      return this.blocks[key].fields.reduce((values, field) => {
        if (field.type === 'template') {
          const template = this.company.templates.filter(t => field.key.replace('template.', '') === t.key).shift();
          if (template) {
            values[field.key] = template;
          }
        } else if (field.type === 'colorPicker') {
          const branding = this.company.branding[field.key.replace('branding.', '')];
          if (branding) {
            values[field.key] = branding;
          } else if (field.defaultValue) {
            values[field.key] = field.defaultValue;
          } else {
            values[field.key] = '#FFFFFF';
          }
        } else {
          values[field.key] = _get(this.company, field.key, null);
        }
        return values;
      }, {});
    },
    async saveValues(key, values) {
      try {
        const block = this.blocks[key];
        if (!block) {
          throw new Error(`Unknown block ${key}`);
        }

        this.formStates[key] = 'SAVING';
        this.formErrs[key] = null;

        const update = block.fields.reduce((update, field) => {
          let value = values[field.key];
          if (field.type.startsWith('asset-') && value && value.id) {
            value = { id: value.id };
          }

          if (field.key.startsWith('branding.')) {
            update.branding = update.branding || {};
            if (field.key.startsWith('branding.dashboardCard')) {
              update.branding.dashboardCard = update.branding.dashboardCard || {};
              update.branding.dashboardCard[field.key.replace('branding.dashboardCard.', '')] = value;
              if (Object.values(update.branding.dashboardCard).reduce((r, v) => r && !v, true)) {
                update.branding.dashboardCard = null;
              }
            } else {
              update.branding[field.key.replace('branding.', '')] = value;
            }
          } else if (field.key.startsWith('preferences.')) {
            update.preferences = update.preferences || {};
            update.preferences[field.key.replace('preferences.', '')] = value;
          } else if (field.key.startsWith('template.')) {
            const { key, lang, value: v } = value;
            update.templates = update.templates || [];
            update.templates.push({ key, lang, value: v });
          } else {
            update[field.key] = value;
          }
          return update;
        }, {});

        console.log(update);
        await this.$apollo.mutate({
          mutation: SettingsMutation,
          variables: { update },
          update(store, { data: { updateCompany: company } }) {
            const data = store.readQuery({ query: SettingsQuery });
            data.company = company;
            store.writeQuery({ query: SettingsQuery, data });
          },
        });

        this.formStates[key] = 'SAVED';
      } catch (err) {
        console.error(err);

        this.formStates[key] = 'ERRORED';
        this.formErrs[key] = `${err}`;
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/settings/Onboarding.vue